import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  TextField,
  Button,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { setLoading } from '../reducers/loadingReducer';
import { fetchUser, updateUser, deleteUser } from '../services/apiService';
import LoadingSpinner from './LoadingSpinner';
import { showToast } from '../reducers/uiReducer';

const AdminUserDetails = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector((state) => state.admin.selectedUser);
  const loading = useSelector((state) => state.loading);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [userLocal, setUser] = useState({
    firstname: '',
    lastname: '',
    email: '',
    role: '',
    isActive: false,
    createdAt: '',
    userId: userId,
  });
  const [errors, setErrors] = useState({});

  const handleEditToggle = () => setEditMode(!editMode);

  const handleUserChange = (e) =>
    setUser({ ...userLocal, [e.target.name]: e.target.value });

  const validate = () => {
    let tempErrors = {};
    tempErrors.firstname = userLocal.firstname ? '' : 'First Name is required.';
    tempErrors.lastname = userLocal.lastname ? '' : 'Last Name is required.';
    tempErrors.email = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(userLocal.email)
      ? ''
      : 'Email is not valid.';
    tempErrors.role = userLocal.role ? '' : 'Role is required.';
    setErrors(tempErrors);
    return Object.values(tempErrors).every((x) => x === '');
  };

  const fetchUserData = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      const userData = await fetchUser(userId);
      setUser({
        firstname: userData.firstname,
        lastname: userData.lastname,
        email: userData.email,
        role: userData.role,
        isActive: userData.isActive,
        createdAt: new Date(userData.createdAt).toLocaleString(),
        userId: userData.userId,
      });
    } catch (error) {
      console.error('Error fetching user data:', error.message);
      dispatch(showToast({ message: 'Failed to fetch user data.', severity: 'error' }));
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, userId]);

  const handleUpdateUser = async () => {
    if (!validate()) return;
    dispatch(setLoading(true));
    try {
      await updateUser(userId, userLocal);
      setEditMode(false);
      dispatch(showToast({ message: 'User updated successfully!', severity: 'success' }));
    } catch (error) {
      console.error('Error updating user:', error.message);
      dispatch(showToast({ message: 'Failed to update user.', severity: 'error' }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleDeleteUser = async () => {
    dispatch(setLoading(true));
    try {
      await deleteUser(userId);
      setShowConfirmDelete(false);
      navigate('/admin/users');
      dispatch(showToast({ message: 'User deleted successfully!', severity: 'success' }));
    } catch (error) {
      console.error('Error deleting user:', error.message);
      dispatch(showToast({ message: 'Failed to delete user.', severity: 'error' }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  return (
    <Box
      sx={{
        margin: 2,
        padding: 3,
        border: '1px solid',
        borderColor: theme.palette.divider,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
          <LoadingSpinner size={30} />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              mb: 2,
              border: '1px solid',
              borderColor: theme.palette.divider,
              borderRadius: 1,
              backgroundColor: theme.palette.action.hover,
              p: 2,
            }}
          >
            <Typography variant="body1"><strong>User ID:</strong> {userLocal.userId}</Typography>
            <Typography variant="body1"><strong>Active:</strong> {userLocal.isActive ? 'Yes' : 'No'}</Typography>
            <Typography variant="body1"><strong>Created At:</strong> {userLocal.createdAt}</Typography>
            <Typography variant="body1"><strong>Last Active:</strong> TBD</Typography>
          </Box>
          <Box component="form">
            <FormLabel component="legend">Account Details</FormLabel>
            <TextField
              fullWidth
              margin="normal"
              label="First Name"
              name="firstname"
              value={userLocal.firstname}
              onChange={handleUserChange}
              disabled={!editMode || loading}
              required
              variant="outlined"
              error={!!errors.firstname}
              helperText={errors.firstname}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Last Name"
              name="lastname"
              value={userLocal.lastname}
              onChange={handleUserChange}
              disabled={!editMode || loading}
              required
              variant="outlined"
              error={!!errors.lastname}
              helperText={errors.lastname}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Email Address"
              name="email"
              value={userLocal.email}
              onChange={handleUserChange}
              disabled={!editMode || loading}
              required
              variant="outlined"
              error={!!errors.email}
              helperText={errors.email}
            />
            <FormControl
              component="fieldset"
              margin="normal"
              disabled={!editMode || loading}
            >
              <FormLabel component="legend">Role</FormLabel>
              <RadioGroup
                name="role"
                value={userLocal.role}
                onChange={handleUserChange}
                row
              >
                <FormControlLabel value="user" control={<Radio />} label="User" />
                <FormControlLabel value="admin" control={<Radio />} label="Admin" />
              </RadioGroup>
            </FormControl>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 2,
              }}
            >
              <Box sx={{ display: 'flex' }}>
                {showConfirmDelete ? (
                  <>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleDeleteUser}
                      sx={{ mr: 2 }}
                      disabled={loading}
                    >
                      Confirm Delete
                    </Button>
                    <Button
                      onClick={() => setShowConfirmDelete(false)}
                      sx={{ mr: 1 }}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setShowConfirmDelete(true)}
                    sx={{ mr: 2 }}
                    disabled={loading}
                  >
                    Delete Account
                  </Button>
                )}
              </Box>
              <Box sx={{ display: 'flex' }}>
                {editMode ? (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleUpdateUser}
                      disabled={loading}
                      sx={{ mr: 2 }}
                    >
                      Save
                    </Button>
                    <Button onClick={handleEditToggle} disabled={loading}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button variant="contained" onClick={handleEditToggle}>
                    Edit
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default AdminUserDetails;